import React, { useState } from "react";
import { Spinner } from "../../../atoms/Spinner";
import { DataTable } from "../../../atoms/DataTable";
import { StatusBadge } from "../../../atoms/StatusBadge";
import { EmptyState } from "../../../atoms/EmptyState";
import {
  useBrevoAccount,
  useBrevoStats,
  useBrevoDomains,
} from "../../../../hooks/providers";

const STAT_PERIODS = [
  { value: 7, label: "7 days" },
  { value: 30, label: "30 days" },
  { value: 90, label: "90 days" },
];

const pct = (num?: number, denom?: number) =>
  denom && denom > 0
    ? `${((Number(num || 0) / denom) * 100).toFixed(1)}%`
    : "—";

const RateCard: React.FC<{ label: string; rate: string; count?: number }> = ({
  label,
  rate,
  count,
}) => (
  <div className="rounded-lg border border-gray-200 p-3">
    <span className="block text-xs uppercase text-gray-500">{label}</span>
    <span className="text-xl font-semibold text-gray-800">{rate}</span>
    {count !== undefined && (
      <span className="ml-1 text-xs text-gray-400">({count})</span>
    )}
  </div>
);

const BrevoPanel: React.FC<{ customer: string; entity: string }> = ({
  customer,
  entity,
}) => {
  const [period, setPeriod] = useState(7);
  const account = useBrevoAccount(customer, entity);
  const stats = useBrevoStats(customer, entity, period);
  const domains = useBrevoDomains(customer, entity);

  const s = stats.data;

  return (
    <div className="space-y-6">
      <section>
        <div className="rounded-lg border border-gray-200 p-4">
          <span className="block text-xs uppercase text-gray-500">
            Email credits remaining
          </span>
          <div className="mt-1 text-lg font-semibold text-gray-800">
            {account.isLoading ? (
              <Spinner size="sm" />
            ) : (
              account.data?.email_credits_remaining ?? "—"
            )}
          </div>
        </div>
      </section>

      <section>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">Sending stats</h3>
          <select
            className="rounded-md border border-gray-300 p-1.5 text-sm"
            value={period}
            onChange={(e) => setPeriod(Number(e.target.value))}
          >
            {STAT_PERIODS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        </div>
        {stats.isLoading ? (
          <Spinner size="sm" />
        ) : !s ? (
          <EmptyState title="No stats available" bordered />
        ) : (
          <>
            <p className="mb-2 text-sm text-gray-600">
              Sent:{" "}
              <span className="font-semibold text-gray-800">{s.sent ?? 0}</span>
            </p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <RateCard
                label="Delivered"
                rate={pct(s.delivered, s.sent)}
                count={s.delivered}
              />
              <RateCard
                label="Hard bounces"
                rate={pct(s.hard_bounces, s.sent)}
                count={s.hard_bounces}
              />
              <RateCard
                label="Soft bounces"
                rate={pct(s.soft_bounces, s.sent)}
                count={s.soft_bounces}
              />
              <RateCard
                label="Blocked"
                rate={pct(s.blocked, s.sent)}
                count={s.blocked}
              />
              {/* Rates need UNIQUE counts to stay <=100%; without them, show the total as a count. */}
              {s.unique_opens != null ? (
                <RateCard
                  label="Open rate"
                  rate={pct(s.unique_opens, s.delivered)}
                  count={s.unique_opens}
                />
              ) : (
                <RateCard label="Opens (total)" rate={String(s.opens ?? 0)} />
              )}
              {s.unique_clicks != null ? (
                <RateCard
                  label="Click rate"
                  rate={pct(s.unique_clicks, s.delivered)}
                  count={s.unique_clicks}
                />
              ) : (
                <RateCard label="Clicks (total)" rate={String(s.clicks ?? 0)} />
              )}
            </div>
          </>
        )}
      </section>

      <section>
        <h3 className="mb-2 text-sm font-semibold text-gray-900">
          Sending domains
        </h3>
        {domains.isLoading ? (
          <Spinner size="sm" />
        ) : (domains.data?.domains ?? []).length === 0 ? (
          <EmptyState title="No sending domain" bordered />
        ) : (
          <>
            <DataTable>
              <DataTable.Head>
                <DataTable.HeadCell>Domain</DataTable.HeadCell>
                <DataTable.HeadCell>Authenticated</DataTable.HeadCell>
              </DataTable.Head>
              <DataTable.Body>
                {domains.data.domains.map((d: any) => (
                  <DataTable.Row key={d.domain}>
                    <DataTable.Cell>{d.domain}</DataTable.Cell>
                    <DataTable.Cell>
                      <StatusBadge
                        color={d.authenticated ? "success" : "gray"}
                        size="xs"
                      >
                        {d.authenticated ? "yes" : "no"}
                      </StatusBadge>
                    </DataTable.Cell>
                  </DataTable.Row>
                ))}
              </DataTable.Body>
            </DataTable>
            <p className="mt-1 text-xs text-gray-500">
              Authenticated is a coarse flag (verified or authenticated on
              Brevo); it does not guarantee full DKIM.
            </p>
          </>
        )}
      </section>
    </div>
  );
};

export default BrevoPanel;
